import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useUserStore } from '../../../stores/userStore';
import { Shield, Crown, User } from 'lucide-react';
import type { UserRole } from '../../../types/user';

interface ProfileSettingsProps {
  user: {
    name: string;
    isNewUser: boolean;
  };
  onUpdate: (data: any) => Promise<void>;
}

export default function ProfileSettings({ user, onUpdate }: ProfileSettingsProps) {
  const { user: authUser } = useAuth();
  const currentUser = useUserStore((state) => state.currentUser);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });

  useEffect(() => {
    if (authUser) {
      setFormData({
        name: authUser.name,
        email: authUser.email,
      });
    }
  }, [authUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await onUpdate({
        name: formData.name,
        email: formData.email
      });
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar perfil');
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleBadge = () => {
    const role = currentUser?.role;
    
    switch (role) {
      case 'admin':
        return (
          <div className="flex items-center space-x-2 bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
            <Shield className="h-4 w-4" />
            <span>Admin</span>
          </div>
        );
      case 'premium':
        return (
          <div className="flex items-center space-x-2 bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full">
            <Crown className="h-4 w-4" />
            <span>Premium</span>
          </div>
        );
      case 'premium_yearly':
        return (
          <div className="flex items-center space-x-2 bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full">
            <Crown className="h-4 w-4" />
            <span>Premium Anual</span>
          </div>
        );
      case 'pro':
        return (
          <div className="flex items-center space-x-2 bg-green-100 text-green-800 px-3 py-1 rounded-full">
            <Crown className="h-4 w-4" />
            <span>Pro</span>
          </div>
        );
      case 'pro_yearly':
        return (
          <div className="flex items-center space-x-2 bg-green-100 text-green-800 px-3 py-1 rounded-full">
            <Crown className="h-4 w-4" />
            <span>Pro Anual</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-2 bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
            <User className="h-4 w-4" />
            <span>Free</span>
          </div>
        );
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Informações do Perfil
        </h2>
        {getRoleBadge()}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome Completo
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </form>
    </div>
  );
}